import { useEffect, useState } from 'react';
import type { ClientWithTasks, TriageItem } from '../../types/models';
import type { AssignmentSuggestion } from '../../lib/ai';
import { Select } from '../ui/Select';
import { CloseIcon, CursorSpark } from '../Icon';
import btn from '../../styles/buttons.module.css';
import form from '../../styles/forms.module.css';
import styles from './AssignModal.module.css';

interface AssignModalProps {
  item: TriageItem;
  clients: ClientWithTasks[];
  /** Claude's suggestion, when available (shown only for low-confidence picks). */
  suggestion?: AssignmentSuggestion | null;
  onClose: () => void;
  onConfirm: (clientId: string, projectId: string, title: string) => void;
}

export function AssignModal({
  item,
  clients,
  suggestion,
  onClose,
  onConfirm,
}: AssignModalProps) {
  const [clientId, setClientId] = useState(suggestion?.clientId ?? '');
  const [projectId, setProjectId] = useState(suggestion?.projectId ?? '');
  const [title, setTitle] = useState(suggestion?.taskTitle || item.title);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const chosen = clients.find((c) => c.id === clientId) ?? null;

  const changeClient = (id: string) => {
    setClientId(id);
    // Reset project unless it still belongs to the newly chosen client.
    const stillValid = clients.find((c) => c.id === id)?.projects.some((p) => p.id === projectId);
    if (!stillValid) setProjectId('');
  };

  const canConfirm = Boolean(clientId) && title.trim().length > 0;
  const confidencePct = suggestion ? Math.round(suggestion.confidence * 100) : 0;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <span className={styles.title}>Assign to client</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.item}>
            <div className={styles.itemSource}>{item.source}</div>
            <div className={styles.itemTitle}>{item.title}</div>
          </div>

          {suggestion && (
            <div className={styles.claude}>
              <div className={styles.claudeHead}>
                <CursorSpark size={13} />
                <span className={styles.claudeLabel}>Claude is unsure</span>
                <span className={`${styles.confidence} ${styles.confidenceLow}`}>
                  {confidencePct}% sure
                </span>
              </div>
              {suggestion.reasoning && <p className={styles.reasoning}>{suggestion.reasoning}</p>}
            </div>
          )}

          <div className={form.field}>
            <label className={form.label}>Client</label>
            <Select
              value={clientId}
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Choose a client…"
              ariaLabel="Client"
              onChange={changeClient}
            />
          </div>

          <div className={form.field}>
            <label className={form.label}>Project</label>
            <Select
              value={projectId}
              options={[
                { value: '', label: 'No project (loose task)' },
                ...(chosen?.projects ?? []).map((p) => ({ value: p.id, label: p.name })),
              ]}
              ariaLabel="Project"
              onChange={setProjectId}
            />
          </div>

          <div className={form.field}>
            <label className={form.label}>Task title</label>
            <input
              className={form.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task title"
              aria-label="Task title"
            />
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${btn.btn} ${btn.cta} ${styles.confirm}`}
            disabled={!canConfirm}
            onClick={() => onConfirm(clientId, projectId, title.trim())}
          >
            Create task
          </button>
        </div>
      </div>
    </div>
  );
}
